import React, {type ReactNode, useEffect, useRef, useState} from 'react'
import axios from 'axios'
import {route} from 'ziggy-js'
import {ClockIcon, PlusIcon} from '@heroicons/react/24/outline'
import {
    conversationTitle,
    conversationWhen,
    recentConversations,
    type RecentConversation,
} from '@/Pages/Events/Assistant/assistantTranscript'

export const iconButtonClass = 'inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-storm-40 hover:bg-storm-90 hover:text-storm-10 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-royal-20 disabled:cursor-no-drop disabled:opacity-50 disabled:hover:bg-transparent'

interface Props {
    eventId: string | null
    organizationWide: boolean
    busy: boolean
    blank: boolean
    currentId: string | null
    onOpen: (id: string) => Promise<boolean>
    onNew: () => void
}

const EventAssistantRecent = ({eventId, organizationWide, busy, blank, currentId, onOpen, onNew}: Props): ReactNode => {
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [status, setStatus] = useState<'ready' | 'error'>('ready')
    const [chats, setChats] = useState<RecentConversation[]>([])
    const tokenRef = useRef(0)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) {
            return
        }

        const dismiss = (): void => {
            tokenRef.current += 1
            setOpen(false)
        }

        const onKey = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') {
                return
            }

            event.stopPropagation()
            dismiss()
        }

        const onPointer = (event: MouseEvent): void => {
            const target = event.target

            if (target instanceof Node && (buttonRef.current?.contains(target) || listRef.current?.contains(target))) {
                return
            }

            dismiss()
        }

        window.addEventListener('keydown', onKey)
        document.addEventListener('mousedown', onPointer)

        return () => {
            window.removeEventListener('keydown', onKey)
            document.removeEventListener('mousedown', onPointer)
        }
    }, [open])

    const toggle = async (): Promise<void> => {
        if (busy) {
            return
        }

        if (open || loading) {
            tokenRef.current += 1
            setOpen(false)
            setLoading(false)

            return
        }

        const token = tokenRef.current + 1
        tokenRef.current = token
        setLoading(true)

        try {
            const response = await axios.get<{conversations?: unknown}>(route('assistant.conversations.index', organizationWide
                ? {scope: 'organization'}
                : {event: eventId}))

            if (tokenRef.current !== token) {
                return
            }

            setChats(recentConversations(response.data.conversations))
            setStatus('ready')
        } catch {
            if (tokenRef.current !== token) {
                return
            }

            setStatus('error')
        }

        setLoading(false)
        setOpen(true)
    }

    const choose = async (id: string): Promise<void> => {
        if (await onOpen(id)) {
            setOpen(false)
        }
    }

    const startNew = (): void => {
        if (busy || blank) {
            return
        }

        tokenRef.current += 1
        setOpen(false)
        setLoading(false)
        onNew()
    }

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                data-testid="event-assistant-recent"
                aria-expanded={open}
                aria-busy={loading}
                aria-controls="event-assistant-recent-list"
                title="Recent chats"
                disabled={busy}
                onClick={() => {
                    void toggle()
                }}
                className={iconButtonClass}
            >
                <ClockIcon className={loading ? 'size-5 animate-pulse' : 'size-5'} aria-hidden="true"/>
                <span className="sr-only">Recent chats</span>
            </button>
            <button
                type="button"
                data-testid="event-assistant-new-chat"
                title="New chat"
                disabled={busy || blank}
                onClick={startNew}
                className={iconButtonClass}
            >
                <PlusIcon className="size-5" aria-hidden="true"/>
                <span className="sr-only">New chat</span>
            </button>
            {open && (
                <div className="pointer-events-none absolute inset-x-0 top-full z-10 overflow-hidden pb-3">
                    <div
                        ref={listRef}
                        id="event-assistant-recent-list"
                        data-testid="event-assistant-recent-list"
                        className="pointer-events-auto border-b border-storm-80 bg-white px-4 py-3 shadow-storm-md motion-safe:animate-assistant-menu-in"
                    >
                        <p className="mb-2 text-xs font-semibold text-storm-40">Recent chats</p>
                        {status === 'error' && (
                            <p role="alert" className="text-xs text-red-700">Recent chats could not be loaded.</p>
                        )}
                        {status === 'ready' && chats.length === 0 && (
                            <p className="text-xs text-storm-40">No recent chats.</p>
                        )}
                        {status === 'ready' && chats.length > 0 && (
                            <ul aria-label="Recent chats" className="max-h-64 overflow-y-auto rounded-lg border border-storm-80">
                                {chats.map((chat) => (
                                    <li key={chat.id}>
                                        <button
                                            type="button"
                                            data-testid={`event-assistant-conversation-${chat.id}`}
                                            aria-current={chat.id === currentId ? 'true' : undefined}
                                            disabled={busy}
                                            onClick={() => {
                                                void choose(chat.id)
                                            }}
                                            className="flex w-full min-w-0 cursor-pointer items-baseline justify-between gap-2 px-3 py-2 text-left hover:bg-storm-90 aria-[current=true]:bg-royal-60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-royal-20 disabled:opacity-50"
                                        >
                                            <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-900">{conversationTitle(chat.title)}</span>
                                            <span className="shrink-0 text-xs text-storm-40">{conversationWhen(chat.updatedAt)}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

export default EventAssistantRecent
